import type { CourtSchema, DateInfo, DaySchema, ScheduleResponse, SlotInfo, SlotStatus, VenueInfo, VenueSchedule } from '../shared/types.js';
import { parseSlotKey } from '../shared/slotKey.js';
import { hourFrom, hourTo, isMyReservation, isSlotWatched, isVenueVisible, isWeekdayVisible, venueBookingUrls, watchedSlots } from './state.js';

const STATUS_TEXT: Record<SlotStatus, string> = {
  available: 'Volno',
  booked: 'Obsazeno',
  closed: 'Zavřeno'
};

function getStatusText(status: SlotStatus): string {
  return STATUS_TEXT[status] || status;
}

export function renderVenueFilter(
  container: HTMLElement,
  venues: VenueInfo[],
  visible: (id: string) => boolean,
  onToggle: (venueId: string, checked: boolean) => void
): void {
  container.innerHTML = '';
  venues.forEach((venue) => {
    const label = document.createElement('label');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = visible(venue.id);
    checkbox.addEventListener('change', () => onToggle(venue.id, checkbox.checked));
    label.appendChild(checkbox);
    label.appendChild(document.createTextNode(venue.name));
    container.appendChild(label);
  });
}

// Monday-first display order; values are JS Date.getDay() numbers
// (0 = Sunday), matching state.ts's visibleWeekdays.
export const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Po' },
  { value: 2, label: 'Út' },
  { value: 3, label: 'St' },
  { value: 4, label: 'Čt' },
  { value: 5, label: 'Pá' },
  { value: 6, label: 'So' },
  { value: 0, label: 'Ne' }
];

const WEEKDAY_LABEL = new Map(WEEKDAYS.map((w) => [w.value, w.label]));

function weekdayOf(date: DateInfo): number {
  return new Date(date.year, date.month - 1, date.day).getDay();
}

// Same checkbox-pill UI as the venue filter, but for days of the week —
// e.g. show only the weekdays one actually plays on.
export function renderDayFilter(
  container: HTMLElement,
  visible: (weekday: number) => boolean,
  onToggle: (weekday: number, checked: boolean) => void
): void {
  container.innerHTML = '';
  WEEKDAYS.forEach(({ value, label: name }) => {
    const label = document.createElement('label');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = visible(value);
    checkbox.addEventListener('change', () => onToggle(value, checkbox.checked));
    label.appendChild(checkbox);
    label.appendChild(document.createTextNode(name));
    container.appendChild(label);
  });
}

export interface DayEntry {
  venue: VenueSchedule;
  day: DaySchema;
}

export type DaysByDate = Map<string, DayEntry[]>;

// Groups by calendar date (not array index) so a venue whose fetch failed
// for one particular day doesn't shift its other days out of alignment with
// the rest.
export function groupByDate(data: ScheduleResponse): DaysByDate {
  const byDate: DaysByDate = new Map();

  data.venues.forEach((venue) => {
    if (!isVenueVisible(venue.id)) return;
    venue.days.forEach((day) => {
      if (!isWeekdayVisible(weekdayOf(day.date))) return;
      const list = byDate.get(day.date.dateString) ?? [];
      list.push({ venue, day });
      byDate.set(day.date.dateString, list);
    });
  });

  return byDate;
}

// "(dnes)" / "(zítra)" / "(za N dny/dní)" next to each day's header, so
// the distance is readable without doing calendar math in one's head.
// Computed from the browser's own clock against the day's local midnight.
function relativeDayLabel(dateString: string, now: Date): string {
  const target = new Date(`${dateString}T00:00:00`);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diff <= 0) return 'dnes';
  if (diff === 1) return 'zítra';
  if (diff <= 4) return `za ${diff} dny`;
  return `za ${diff} dní`;
}

// Every date the current filters leave visible, oldest first — the day
// picker's contents, and the list a single-day selection is clamped to.
// Takes the grouping rather than the raw data so a render builds it once.
export function availableDates(byDate: DaysByDate): string[] {
  return [...byDate.keys()].sort();
}

// A phone can't usefully show three weeks of wide tables at once, so below
// this width only the selected day renders and the day picker above it does
// the navigating. The one definition of that breakpoint on the JS side —
// main.ts listens to this same object for crossings, so the render mode and
// the re-render can't drift apart. Keep in sync with the matching @media in
// styles.css, which plain CSS gives no way to share.
export const SINGLE_DAY_QUERY = window.matchMedia('(max-width: 760px)');

export function isSingleDayMode(): boolean {
  return SINGLE_DAY_QUERY.matches;
}

// The mobile day picker: ‹ / › step one day, and between them a
// horizontally scrollable strip of every available day to jump straight to.
// Rendered empty (and hidden by CSS) outside single-day mode, where the
// schedule simply lists every day. Clicks are delegated in main.ts, via the
// data-date / data-step attributes.
// Same memo as the schedule below: without it the strip was re-serialized
// and its DOM replaced on every poll, which also reset its horizontal
// scroll to the left edge once a minute.
let lastDayNavHtml: string | null = null;

export function renderDayNav(container: HTMLElement, dates: string[], now: Date): void {
  if (dates.length === 0) {
    if (lastDayNavHtml === '') return;
    lastDayNavHtml = '';
    container.innerHTML = '';
    return;
  }

  const chips = dates
    .map((dateString) => {
      const weekday = new Date(`${dateString}T00:00:00`).getDay();
      const [, month, day] = dateString.split('-').map(Number);
      const relative = relativeDayLabel(dateString, now);
      // Only "dnes"/"zítra" earn the extra line; "za 9 dní" is already
      // obvious from the date on a chip this small.
      const relativeHtml = relative === 'dnes' || relative === 'zítra' ? `<span class="day-chip-rel">${relative}</span>` : '';
      return (
        `<button type="button" class="day-chip" data-date="${dateString}">` +
        `<span class="day-chip-wd">${escapeHtml(WEEKDAY_LABEL.get(weekday) ?? '')}</span>` +
        `<span class="day-chip-date">${day}.${month}.</span>` +
        `${relativeHtml}</button>`
      );
    })
    .join('');

  const markup =
    `<button type="button" class="day-nav-arrow" data-step="-1" aria-label="Předchozí den">‹</button>` +
    `<div class="day-nav-strip">${chips}</div>` +
    `<button type="button" class="day-nav-arrow" data-step="1" aria-label="Další den">›</button>`;
  if (markup === lastDayNavHtml) return;
  lastDayNavHtml = markup;
  container.innerHTML = markup;
}

// Which chip reads as current, and whether the arrows have anywhere to go.
// Applied to the existing DOM rather than baked into the markup above: on
// desktop this follows the day being scrolled past, and rebuilding the whole
// strip for that would throw away its own horizontal scroll position several
// times a second.
export function setDayNavSelection(container: HTMLElement, dates: string[], selected: string | null): void {
  container.querySelectorAll<HTMLElement>('.day-chip').forEach((chip) => {
    chip.classList.toggle('selected', chip.dataset.date === selected);
  });
  const index = selected ? dates.indexOf(selected) : -1;
  container.querySelectorAll<HTMLButtonElement>('.day-nav-arrow').forEach((arrow) => {
    const step = Number(arrow.dataset.step);
    const target = index + step;
    arrow.disabled = index < 0 || target < 0 || target >= dates.length;
  });
}

// The markup the schedule was last painted with. The server re-scrapes
// every minute, so this used to rebuild ~50k DOM nodes that minute whether
// or not a single slot had changed — several seconds of frozen main thread
// on an older iPhone, every minute. Comparing the generated string first
// turns the common "nothing actually changed" refresh into a no-op.
// The structure of the last render — the warnings block plus the exact ordered
// list of days and week dividers. While this is unchanged, a refresh can
// patch the day cards whose contents actually differ and leave every other
// card's element untouched, which is what keeps clicking a cell from
// disturbing the page: replacing the whole schedule made all 21 sections new
// elements, so content-visibility forgot their measured heights, the document
// briefly collapsed to the placeholder sizes, and the scroll position landed
// somewhere else entirely.
let lastStructure: string | null = null;
// date -> that card's inner markup, as last painted.
let lastDayInner = new Map<string, string>();

// `onlyDate` narrows the output to that single day (mobile — see
// isSingleDayMode); null renders every day the filters leave visible.
export function renderData(
  content: HTMLElement,
  data: ScheduleResponse,
  byDate: DaysByDate,
  allDates: string[],
  onlyDate: string | null = null
): void {
  const dates = onlyDate === null ? allDates : byDate.has(onlyDate) ? [onlyDate] : [];
  const now = new Date();
  const withPointerExtras = pointerIsFine();

  const warnings = renderVenueWarnings(data);
  const inner = new Map<string, string>();
  const separators = new Map<string, string>();

  dates.forEach((dateString, index) => {
    const entries = byDate.get(dateString)!;
    const dateHeader = entries[0].day.dateHeader;

    // A labelled divider before each Monday (except when the list starts
    // with one) — with up to three weeks of day cards, the week boundaries
    // are otherwise easy to lose track of while scrolling.
    if (index > 0 && new Date(`${dateString}T00:00:00`).getDay() === 1) {
      const [, month, day] = dateString.split('-').map(Number);
      separators.set(dateString, `<div class="week-separator">Týden od ${day}. ${month}.</div>`);
    }

    inner.set(
      dateString,
      `<div class="day-header">` +
        `<div class="day-title">${escapeHtml(dateHeader)} <span class="day-relative">(${escapeHtml(relativeDayLabel(dateString, now))})</span></div>` +
        `</div>` +
        `<div class="day-scroll">${renderScheduleTable(entries, withPointerExtras)}</div>`
    );
  });

  const structure = JSON.stringify([warnings, dates, [...separators.keys()]]);

  // Same days in the same order as last time: touch only the cards that
  // differ, leaving every other section element (and the layout around it)
  // exactly as it was.
  if (structure === lastStructure && content.querySelector('.day-section')) {
    const patches: [HTMLElement, string][] = [];
    let complete = true;
    for (const dateString of dates) {
      const next = inner.get(dateString)!;
      if (next === lastDayInner.get(dateString)) continue;
      const section = content.querySelector<HTMLElement>(`.day-section[data-date="${CSS.escape(dateString)}"]`);
      if (!section) {
        complete = false;
        break;
      }
      patches.push([section, next]);
    }
    if (complete) {
      patches.forEach(([section, next]) => {
        section.innerHTML = next;
      });
      lastDayInner = inner;
      return;
    }
  }

  let html = warnings;
  dates.forEach((dateString) => {
    const entries = byDate.get(dateString)!;
    html += separators.get(dateString) ?? '';
    // A rough measured-height stand-in for while this card is scrolled out
    // of view and content-visibility is skipping its layout. Close to the
    // real thing matters: a placeholder far off the mark (it used to be one
    // flat 260px for every card) makes the scrollbar lie and the page shift
    // as cards are scrolled into view and take their true size.
    const rows = entries.reduce((total, entry) => total + entry.day.courts.length + 3, 0);
    html +=
      `<div class="day-section" data-date="${dateString}" style="contain-intrinsic-size:auto ${44 + rows * 17}px">` +
      `${inner.get(dateString)}</div>`;
  });

  const emptyText =
    onlyDate !== null && allDates.length > 0
      ? 'Pro tento den nejsou data — vyber jiný den nahoře.'
      : 'Nic k zobrazení — zkontrolujte filtry lokací a dnů v panelu nahoře.';
  lastStructure = structure;
  lastDayInner = inner;
  content.innerHTML = html || `<div class="schedule-empty"><p>${emptyText}</p></div>`;
}

// Surfaces per-venue upstream fetch failures (e.g. Cloudflare rejecting a
// request for one particular day) so a partially-incomplete picture is
// visible instead of silently missing days with no explanation — full list
// of failures goes in the title tooltip, same pattern as the slot cells use.
function renderVenueWarnings(data: ScheduleResponse): string {
  const withErrors = data.venues.filter((v) => isVenueVisible(v.id) && v.errors.length > 0);
  if (withErrors.length === 0) return '';

  const items = withErrors
    .map((v) => `<li><strong>${escapeHtml(v.name)}</strong>: ${v.errors.length} chyb při načítání <span title="${escapeHtml(v.errors.join('\n'))}">(najet myší pro detail)</span></li>`)
    .join('');

  return `<div class="venue-warning">⚠️ Některá data se nepodařilo načíst:<ul>${items}</ul></div>`;
}

// Whether a time slot has already elapsed, for cells with no real slot data
// at all (e.g. a venue not open yet at that hour while another one is) —
// the clock doesn't care whether the venue was open, so these still need
// the past overlay just like real slots do. Real slots get this from the
// server (see shared/types.ts's SlotInfo.isPast); this is the client-side
// equivalent for the synthetic "nothing here" cells, using the browser's
// own clock (which, unlike the server, has no ambiguity about timezone —
// it's simply whatever the user's machine says right now).
function isTimeSlotPast(date: DateInfo, time: string, now: Date): boolean {
  const [hours, minutes] = time.split(':').map(Number);
  const end = new Date(date.year, date.month - 1, date.day, hours, minutes + 30);
  return end <= now;
}

// The venue heading links to that venue's real booking page (new tab),
// where the actual reservation happens. Only the heading: every court of a
// venue books through the same page, so linking each court row as well was
// just the same link repeated down the table. Its cell carries no
// data-row/data-col, so dragSelect ignores clicks/touches on it and the
// native link behavior is safe. Falls back to plain text before
// /api/venues has loaded.
function bookingLink(venueId: string, label: string): string {
  const url = venueBookingUrls.get(venueId);
  if (!url) return escapeHtml(label);
  return `<a class="booking-link" href="${escapeHtml(url)}" target="_blank" rel="noopener" title="Otevřít rezervační stránku">${escapeHtml(label)}</a>`;
}

// True on a device with a real pointer. The schedule is tens of thousands
// of cells, and two per-cell features exist only for that pointer: the
// hover tooltip (`title`) and the corner reservation dot (a <button>).
// Emitting them on touch cost an older iPhone ~17k extra elements plus as
// many long attribute strings, for markup nothing there can ever use —
// touch marks a reservation by long-press, and the purple frame is the
// indicator. The query object is made once, but .matches is read on every
// render, so it follows a device that changes (e.g. an iPad gaining a
// trackpad).
const POINTER_FINE_QUERY = window.matchMedia?.('(hover: hover)') ?? null;

function pointerIsFine(): boolean {
  return POINTER_FINE_QUERY?.matches ?? true;
}

// One combined table per day: a compact divider row per venue, its court
// rows, then its price row — all sharing one fixed time-slot column layout
// derived from the configured hour range (see the sidebar's hour filter),
// NOT from whatever slots the venues happen to have. Every day-table thus
// has an identical column count (and, with the fixed per-column width in
// CSS, an identical width); data outside the range is simply not rendered,
// and in-range times a venue doesn't offer fall through to the existing
// "no data here" cells. Times are unpadded ("7:00"), matching upstream
// slot keys.
function renderScheduleTable(entries: DayEntry[], withPointerExtras: boolean): string {
  const now = new Date();
  const allTimeSlots: string[] = [];
  for (let h = hourFrom; h < hourTo; h++) {
    allTimeSlots.push(`${h}:00`, `${h}:30`);
  }

  const columnCount = allTimeSlots.length + 1; // + court-name column

  // The hour labels, repeated above every venue's courts rather than once
  // at the top of the table. With seven venues stacked in one day-table,
  // a single top row left the lower courts with no visible time reference
  // at all — you had to scroll back up to tell which column was which.
  let hourHeader = '<tr class="hours-row"><th class="court-name-header"></th>';
  for (let i = 0; i < allTimeSlots.length; i += 2) {
    const hour = allTimeSlots[i].split(':')[0];
    const span = Math.min(2, allTimeSlots.length - i);
    hourHeader += `<th colspan="${span}">${hour}:00</th>`;
  }
  hourHeader += '</tr>';

  let html = '<table class="schedule-table">';

  let rowIndex = 0; // shared across every venue's court rows in this table, for rectangle drag-select

  entries.forEach(({ venue, day }) => {
    if (day.courts.length === 0) return;

    // The name sits in a sticky inline-block span, not a sticky td — the
    // cell spans the whole (wide) table, so sticky on the td itself would
    // do nothing and the text would still scroll out of view.
    html += `<tr class="venue-row"><td class="venue-name" colspan="${columnCount}"><span class="venue-name-inner">${bookingLink(venue.id, venue.name)} <span class="venue-count">(${day.summary.available} volných)</span></span></td></tr>`;
    html += hourHeader;

    day.courts.forEach((court: CourtSchema) => {
      html += '<tr>';
      html += `<td class="court-name">${escapeHtml(court.name)}</td>`;

      allTimeSlots.forEach((time, colIndex) => {
        const slot = court.slots[time];

        if (slot) {
          const isWatched = isSlotWatched(venue.id, day.date.dateString, court.id, time);
          const isReserved = slot.status !== 'closed' && isMyReservation(venue.id, day.date.dateString, court.id, time);
          const watchedClass = isWatched ? 'watched' : '';
          const reservedClass = isReserved ? 'reserved' : '';
          const pastClass = slot.isPast ? 'past' : '';
          const statusText = getStatusText(slot.status) + (slot.isPast ? ' (uplynulo)' : '');
          // Only built for pointer devices — a title is a hover tooltip, and
          // on touch it was thousands of multi-line strings nothing displays.
          const titleAttr = withPointerExtras
            ? ` title="${escapeHtml(
                slot.timeDisplay
                  ? `${slot.timeDisplay}\n${statusText}${slot.price ? `\n${slot.price} Kč` : ''}${isWatched ? '\n🔔 Sledováno' : ''}${isReserved ? '\n🟣 Moje rezervace' : ''}`
                  : statusText
              )}"`
            : '';

          // Anything except a closed slot can be "my reservation" — booked,
          // or still-available (reserved outside the booking system, or
          // before it caught up) — a small corner button,
          // deliberately outside the main cell click area so it doesn't
          // collide with the whole-cell click/drag that toggles watching
          // (see dragSelect.ts's early-return on .reservation-mark, and
          // main.ts's own delegated click handler for it). On touch it's
          // also just a visual indicator once set; marking there instead
          // happens via a long-press that doesn't turn into a drag.
          const reservationHtml = withPointerExtras && slot.status !== 'closed' ? `<button type="button" class="reservation-mark${isReserved ? ' set' : ''}" title="${isReserved ? 'Zrušit označení jako moje rezervace' : 'Označit jako moje rezervace'}"></button>` : '';

          html += `<td${titleAttr} class="slot-td" data-row="${rowIndex}" data-col="${colIndex}" data-venue="${venue.id}" data-date="${day.date.dateString}" data-court="${court.id}" data-time="${time}" data-status="${slot.status}" data-past="${slot.isPast ? '1' : ''}"><div class="slot-cell ${slot.status} ${pastClass} ${watchedClass} ${reservedClass}">${reservationHtml}</div></td>`;
        } else {
          // No data for this court at this time at all (e.g. outside its
          // opening hours while another court/venue is open then) — not
          // selectable/watchable, but still part of the drag-select grid
          // (so a rectangle dragged across it doesn't look like it has a
          // hole), still worth a tooltip on hover, and still gets the past
          // overlay once its time has gone by regardless of the venue
          // never having been open then.
          const pastClass = isTimeSlotPast(day.date, time, now) ? 'past' : '';
          const closedTitle = withPointerExtras ? ` title="${escapeHtml(getStatusText('closed'))}"` : '';
          html += `<td${closedTitle} data-row="${rowIndex}" data-col="${colIndex}"><div class="slot-cell closed ${pastClass}"></div></td>`;
        }
      });

      html += '</tr>';
      rowIndex++;
    });

    // A venue that publishes no per-slot prices at all (HAMR — its grid
    // carries none, and its price lists are seasonal JPEGs) would otherwise
    // get a row of blanks exactly where someone goes looking for the price.
    // The picture goes there instead, behind a button that opens it.
    const hasPrices = allTimeSlots.some((time) => day.priceMap?.[time] != null);
    if (!hasPrices && venue.priceList) {
      html +=
        `<tr class="price-row"><td colspan="${columnCount}" class="price-list-cell">` +
        `<span class="venue-name-inner">` +
        `<button type="button" class="price-list-btn" data-pricelist-url="${escapeHtml(venue.priceList.url)}" ` +
        `data-pricelist-title="${escapeHtml(venue.priceList.title)}">💰 Ceník (obrázek)</button>` +
        `</span></td></tr>`;
    } else {
      // Prices are quoted upstream per half-hour tick, but the merged header
      // above groups two ticks under one hour label — so the displayed number
      // has to be the SUM of both ticks' prices to actually mean "cost of this
      // hour" (a plain single-tick price, as this used to show, reads as half
      // the real hourly rate). Summing also stays correct across a peak/
      // off-peak split that falls mid-hour (e.g. Pankrác's 250/125 split),
      // where the two ticks genuinely have different prices.
      html += '<tr class="price-row"><td></td>';
      for (let i = 0; i < allTimeSlots.length; i += 2) {
        const span = Math.min(2, allTimeSlots.length - i);
        const pair = allTimeSlots.slice(i, i + span);
        const prices = pair.map((t) => day.priceMap?.[t]).filter((p): p is number => p != null);
        const price = prices.length > 0 ? `${prices.reduce((sum, p) => sum + p, 0)},-` : '';
        html += `<td colspan="${span}">${price}</td>`;
      }
      html += '</tr>';
    }
  });

  html += '</table>';
  return html;
}

interface WatchRow {
  key: string;
  date: string;
  time: string;
  venueName: string;
  courtName: string;
  dateHeader: string;
  // null when the slot isn't in the current snapshot (that venue/day failed
  // to fetch, the date has scrolled out of range, or nothing's loaded yet).
  slot: SlotInfo | null;
}

function timeMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function buildWatchRows(data: ScheduleResponse | null): WatchRow[] {
  const rows: WatchRow[] = [];
  watchedSlots.forEach((key) => {
    const { venueId, date, courtId, time } = parseSlotKey(key);
    const venue = data?.venues.find((v) => v.id === venueId);
    const day = venue?.days.find((d) => d.date.dateString === date);
    const court = day?.courts.find((c) => c.id === courtId);
    rows.push({
      key,
      date,
      time,
      venueName: venue?.name ?? venueId,
      courtName: court?.name ?? `Kurt ${courtId}`,
      dateHeader: day?.dateHeader ?? date,
      slot: court?.slots[time] ?? null
    });
  });
  return rows.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      timeMinutes(a.time) - timeMinutes(b.time) ||
      a.venueName.localeCompare(b.venueName) ||
      a.courtName.localeCompare(b.courtName)
  );
}

function describeWatchStatus(slot: SlotInfo | null): { text: string; cls: string } {
  if (!slot) return { text: 'mimo načtená data', cls: 'unknown' };
  if (slot.isPast) return { text: 'uplynulo', cls: 'past' };
  if (slot.status === 'available') return { text: '✅ Volno!', cls: 'freed' };
  return { text: getStatusText(slot.status), cls: slot.status };
}

// The sidebar list of everything the current email watches, with slots that
// are free right now called out. Clicking a row jumps to its cell in the
// schedule (see focusSlot), the ✕ unwatches it — both handled by delegation
// in main.ts via the data-key / data-action attributes.
export function renderWatchList(container: HTMLElement, data: ScheduleResponse | null): void {
  const rows = buildWatchRows(data);
  if (rows.length === 0) {
    container.innerHTML =
      '<p class="watch-list-empty">Zatím nic nesledujete.<br>Klikni nebo táhni přes <strong>obsazené</strong> sloty v rozvrhu — až se uvolní, přijde email.</p>';
    return;
  }

  container.innerHTML = rows
    .map((row) => {
      const status = describeWatchStatus(row.slot);
      return (
        `<div class="watch-item ${status.cls}" data-key="${escapeHtml(row.key)}" title="Zobrazit v rozvrhu">` +
        `<div class="watch-item-body">` +
        `<div class="watch-item-when">${escapeHtml(row.dateHeader)} ${escapeHtml(row.time)}</div>` +
        `<div class="watch-item-where">${escapeHtml(row.venueName)} · ${escapeHtml(row.courtName)}</div>` +
        `<div class="watch-item-status">${escapeHtml(status.text)}</div>` +
        `</div>` +
        `<button class="watch-item-remove" data-action="remove" title="Zrušit sledování">✕</button>` +
        `</div>`
      );
    })
    .join('');
}

// Scrolls the schedule to the cell for `key` and flashes it. Returns false
// when the cell isn't rendered at all (venue filtered out, or that day isn't
// in the snapshot).
export function focusSlot(content: HTMLElement, key: string): boolean {
  const { venueId, date, courtId, time } = parseSlotKey(key);
  const td = content.querySelector<HTMLElement>(
    `td.slot-td[data-venue="${CSS.escape(venueId)}"][data-date="${CSS.escape(date)}"]` +
      `[data-court="${CSS.escape(courtId)}"][data-time="${CSS.escape(time)}"]`
  );
  if (!td) return false;

  td.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  const cell = td.querySelector<HTMLElement>('.slot-cell');
  if (cell) {
    cell.classList.remove('focus-flash');
    void cell.offsetWidth; // restart the animation if a previous flash is still running
    cell.classList.add('focus-flash');
    setTimeout(() => cell.classList.remove('focus-flash'), 2000);
  }
  return true;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
